/**
 * Analytic two-bone IK solver (triangle solver).
 *
 * Given a 2-bone chain (e.g. upper arm → lower arm → hand, or
 * upper leg → lower leg → foot), computes the joint angles that
 * place the end effector at a target position, using a pole vector
 * to control the bend plane.
 *
 * This mirrors the TriSolver concept from the Python schema.
 */

import { Matrix4, Quaternion, Vector3 } from 'three';

const _v0 = new Vector3();
const _v1 = new Vector3();
const _v2 = new Vector3();
const _vTarget = new Vector3();
const _vPole = new Vector3();
const _quat = new Quaternion();
const _mat = new Matrix4();

export type TwoBoneIKResult = {
  rootLocal: Matrix4;
  midLocal: Matrix4;
};

/**
 * Solve two-bone IK analytically.
 *
 * @param rootWorldPos  World position of the root joint (e.g. shoulder)
 * @param midWorldPos   World position of the middle joint (e.g. elbow)
 * @param endWorldPos   World position of the end joint (e.g. wrist)
 * @param targetPos     Desired world position for the end effector
 * @param poleTarget    World position of the pole vector target (controls bend direction)
 * @param rootLength    Length of the first bone (root → mid)
 * @param midLength     Length of the second bone (mid → end)
 * @param rootParentWorldInv  Inverse of root bone's parent world transform
 * @param midParentWorldInv   Inverse of mid bone's parent world transform
 */
export function solveTwoBoneIK(
  rootWorldPos: Vector3,
  midWorldPos: Vector3,
  endWorldPos: Vector3,
  targetPos: Vector3,
  poleTarget: Vector3,
  rootLength: number,
  midLength: number,
  rootParentWorldInv: Matrix4,
  midParentWorldInv: Matrix4,
): TwoBoneIKResult | null {
  const a = rootLength;
  const b = midLength;

  _vTarget.copy(targetPos).sub(rootWorldPos);
  let dist = _vTarget.length();

  if (dist < 1e-8) return null;

  const maxReach = a + b - 1e-4;
  const minReach = Math.abs(a - b) + 1e-4;
  dist = Math.max(minReach, Math.min(maxReach, dist));

  const cosAngleRoot = (a * a + dist * dist - b * b) / (2 * a * dist);
  const cosAngleMid = (a * a + b * b - dist * dist) / (2 * a * b);

  const angleRoot = Math.acos(Math.max(-1, Math.min(1, cosAngleRoot)));
  const angleMid = Math.PI - Math.acos(Math.max(-1, Math.min(1, cosAngleMid)));

  const targetDir = _v0.copy(_vTarget).normalize();

  _vPole.copy(poleTarget).sub(rootWorldPos);
  const polePlaneNormal = _v1.crossVectors(targetDir, _vPole);
  if (polePlaneNormal.lengthSq() < 1e-10) {
    polePlaneNormal.set(0, 0, 1);
  }
  polePlaneNormal.normalize();

  const bendAxis = polePlaneNormal;

  _quat.setFromAxisAngle(bendAxis, -angleRoot);
  const rootToMidDir = _v2.copy(targetDir).applyQuaternion(_quat).normalize();

  const midToEndDir = _vTarget
    .copy(targetPos)
    .sub(rootWorldPos.clone().add(rootToMidDir.clone().multiplyScalar(a)))
    .normalize();

  const rootLocal = computeLocalRotation(
    rootWorldPos,
    rootToMidDir,
    rootParentWorldInv,
    rootWorldPos,
    midWorldPos,
  );

  const newMidPos = rootWorldPos.clone().add(rootToMidDir.clone().multiplyScalar(a));
  const midLocal = computeLocalRotation(
    newMidPos,
    midToEndDir,
    midParentWorldInv,
    midWorldPos,
    endWorldPos,
  );

  return { rootLocal, midLocal };
}

function computeLocalRotation(
  jointWorldPos: Vector3,
  newDir: Vector3,
  parentWorldInv: Matrix4,
  currentJointPos: Vector3,
  currentChildPos: Vector3,
): Matrix4 {
  const currentDir = _v0.subVectors(currentChildPos, currentJointPos).normalize();
  _quat.setFromUnitVectors(currentDir, newDir);

  const worldRot = new Matrix4().makeRotationFromQuaternion(_quat);

  const localRot = new Matrix4()
    .copy(parentWorldInv)
    .multiply(worldRot)
    .multiply(new Matrix4().copy(parentWorldInv).invert());

  return localRot;
}

/**
 * Compute a default pole target for a limb chain.
 * Uses the current bend direction of the middle joint.
 */
export function defaultPoleTarget(
  rootPos: Vector3,
  midPos: Vector3,
  endPos: Vector3,
  offset = 1.0,
): Vector3 {
  const chainDir = _v0.subVectors(endPos, rootPos).normalize();
  const midOffset = _v1.subVectors(midPos, rootPos);
  const projected = chainDir.clone().multiplyScalar(midOffset.dot(chainDir));
  const bendDir = _v2.subVectors(midOffset, projected);

  if (bendDir.lengthSq() < 1e-8) {
    bendDir.set(0, 0, -1);
  }
  bendDir.normalize();

  const chainMid = rootPos.clone().add(endPos).multiplyScalar(0.5);
  return chainMid.add(bendDir.multiplyScalar(offset));
}
